import React, { useContext, useState, useCallback, useRef, useEffect } from "react";
import { MappingContext } from "../Contexts/MappingContext";
import Search from "Components/DesignSystem/Search/Search";
import Spinner from "Components/DesignSystem/Spinner/Spinner";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";

type AttributeSelectorListProps = {
    selectedAttribute?: MappingAttribute | null;
    setSelectedAttribute?: (attribute: MappingAttribute | null) => void;
};

const AttributeSelectorList: React.FC<AttributeSelectorListProps> = ({
    selectedAttribute: propSelectedAttribute,
    setSelectedAttribute: propSetSelectedAttribute
}) => {
    const context = useContext(MappingContext);
    const [searchTerm, setSearchTerm] = useState("");
    const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

    // Use props if provided, otherwise get from context
    const selectedAttribute = propSelectedAttribute !== undefined
        ? propSelectedAttribute
        : context.selectedAttribute;

    const setSelectedAttribute = propSetSelectedAttribute !== undefined
        ? propSetSelectedAttribute
        : context.setSelectedAttribute;

    // Get attributes list
    const attributes = context.mappingState.working.attributes;

    // Get pagination state and load more function for attributes
    const attributePagination = context.pagination.attributes;
    const hasMore = attributePagination.hasMore;
    const loading = attributePagination.loading;

    // Check if attribute values are loading - disable selection during this time
    const isLoadingAttributeValues = context.isLoadingAttributeValues;

    // Load more attributes when scrolling (only if not searching)
    const loadMoreAttributesHandler = () => {
        if (searchTerm.trim()) return; // Don't load more when searching

        // Use the dedicated loadMoreAttributes function from context
        // This works regardless of which tab is active
        context.loadMoreAttributes();
    };

    // Use infinite scroll hook
    const sentinelRef = useInfiniteScroll(loadMoreAttributesHandler, hasMore, loading);

    /*
      § 288 — the sidebar's own search-busy flag. `loading` above is
      `pagination.attributes.loading`, which the infinite-scroll page loader sets too,
      so a field wired to it would spin while the user scrolled the attribute list.
      This one spans exactly the wait the user caused: true from the keystroke, false
      when the search this component asked for settles.
    */
    const [searchBusy, setSearchBusy] = useState(false);

    // Debounced server-side search
    const handleSearchChange = useCallback((value: string) => {
        setSearchTerm(value);
        setSearchBusy(true);

        // Clear previous debounce
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }

        // Debounce the API call
        searchDebounceRef.current = setTimeout(() => {
            // Same call, same arguments — only its settlement is observed (§ 288).
            Promise.resolve(context.searchAttributesList(value)).finally(() => setSearchBusy(false));
        }, 500);
    }, [context]);

    // Cleanup debounce on unmount
    useEffect(() => {
        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
    }, []);

    // Reset local search when the context signals (e.g. after a successful save)
    const resetSignal = context.listSearchResetSignal;
    const didMountRef = useRef(false);
    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        setSearchTerm('');
    }, [resetSignal]);

    const handleAttributeClick = (attribute: MappingAttribute) => {
        // Block ALL clicks while loading - strict prevention
        if (isLoadingAttributeValues) {
            return;
        }

        // Don't allow clicking the same attribute (prevent deselect and reload)
        if (selectedAttribute?.attribute === attribute.attribute) {
            return;
        }

        // Clear table search when switching attributes — previous search is irrelevant
        context.onSearchTableData('');

        // Select the new attribute
        setSelectedAttribute(attribute);
    };

    return (
        <div className="tw-w-56 tw-inline-flex tw-flex-col tw-justify-start tw-items-center tw-border tw-border-edge">
            {/* Search Bar — DS Search (2026-09-05); the 500ms server-side search
                debounce lives in handleSearchChange, unchanged. */}
            <div className="tw-w-52 tw-mt-3 tw-mb-2">
                <Search
                    value={searchTerm}
                    onChange={(value) => handleSearchChange(value)}
                    /* § 288 — the search-only flag, NOT `loading`: that one covers the
                       infinite-scroll page loads as well. */
                    loading={searchBusy}
                    loadingLabel="Searching attributes"
                    placeholder="Search"
                    ariaLabel="Search attributes"
                    size="sm"
                    data-testid="components-attributeselectorlist-input-1"
                />
            </div>

            {/* Attribute List */}
            <div className="tw-w-full tw-flex tw-flex-col tw-gap-0.5 tw-overflow-y-auto tw-h-[calc(100vh-200px)] tw-min-h-[calc(100vh-200px)] tw-pb-2">
                {attributes.map((attribute) => {
                    const isSelected = selectedAttribute?.attribute === attribute.attribute;
                    const displayName = attribute.attribute || attribute.mapped_attribute || '';

                    return (
                        <div role="button"
                            key={attribute.attribute}
                            onClick={() => handleAttributeClick(attribute)}
                            className={`tw-self-stretch tw-h-6 tw-px-2 tw-py-1 tw-inline-flex tw-justify-start tw-items-center tw-gap-2.5 tw-transition-colors ${
                                isLoadingAttributeValues
                                    ? 'tw-cursor-not-allowed tw-opacity-50 tw-pointer-events-none'
                                    : 'tw-cursor-pointer hover:tw-bg-accent-tint'
                            } ${isSelected ? 'tw-bg-accent-tint tw-rounded' : ''}`}
                            data-testid={`attribute-item-${attribute.attribute}`}
                        >
                            {/* Status Indicator — static green. Every row is reviewed+considered
                                at the parent attribute level (the BE allow-list already gates on
                                that), so the dot is purely a visual marker, not a state flag. */}
                            <div className="tw-w-3.5 tw-h-3.5 tw-rounded-3xl tw-border tw-bg-tone-success-glyph tw-border-tone-success-ink" />

                            {/* Attribute Name */}
                            <div
                                className={`tw-flex-1 tw-min-w-0 tw-truncate tw-text-sm ${isSelected
                                    ? 'tw-text-accent tw-font-semibold'
                                    : 'tw-text-ink tw-font-normal'
                                    }`}
                                style={{ fontFamily: "Noto Sans" }}
                                title={displayName}
                            >
                                {displayName}
                            </div>
                        </div>
                    );
                })}

                {/* Infinite scroll sentinel (only show when not searching) */}
                {!searchTerm.trim() && <div ref={sentinelRef} style={{ height: '1px' }} />}

                {/* Loading indicator */}
                {loading && (
                    <div className="tw-flex tw-justify-center tw-items-center tw-p-2 tw-gap-2">
                        <Spinner size="sm" label="Loading attributes" />
                        <span className="tw-text-xs tw-text-ink-tertiary">Loading...</span>
                    </div>
                )}

                {/* All loaded message */}
                {!hasMore && !loading && !searchTerm.trim() && attributes.length > 0 && (
                    <div className="tw-text-center tw-p-2 tw-text-ink-tertiary tw-text-xs">
                        All {attributes.length} attributes loaded
                    </div>
                )}

                {/* No Results Message */}
                {attributes.length === 0 && !loading && (
                    <div className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-px-2 tw-text-center tw-text-ink-tertiary tw-text-sm tw-italic">
                        No attributes found
                    </div>
                )}
            </div>
        </div>
    );
};

export default AttributeSelectorList;
